import * as React from 'react'
import { closeNewsCenterModal } from '../../store/modals/actions'
import ModalHeader from './Parts/ModalHeader'
import { AppState } from '../../store'
import { AnyAction, Dispatch } from 'redux'
import { connect } from 'react-redux'
import ModalWindow from './Parts/ModalWindow'
import ModalMiddle from './Parts/ModalMiddle'
import ModalContent from './Parts/ModalContent'
import { useTranslation } from 'react-i18next'
import ModalLoader from './Parts/ModalLoader'
import Button from '../Button/Button'
import { NewsCentersController } from '../../controllers'
import { CurrentUser, DisplayableError, NewsCenter, Project } from '../../types'
import TooltipError from '../Tooltips/ErrorTooltip'
import CheckboxInput from '../Form/CheckboxInput'

export type NewsCenterModalTab = 'details' | 'notes' | 'planning'

interface IStateToProps {
  currentUser: CurrentUser
  newsCenter?: NewsCenter
  onSubmit: (newsCenter: NewsCenter) => void
}

interface IDispatchToProps {
  close: typeof closeNewsCenterModal
}

type IProps = IDispatchToProps & IStateToProps

interface IState {
  didInitialLoad: boolean
  newsCenter: NewsCenter
  errors: DisplayableError[]
}

const NewsCenterModal = (props: IProps) => {
  const { t } = useTranslation()
  const { currentUser } = props
  const [state, setState] = React.useState<IState>({
    didInitialLoad: false,
    newsCenter: null,
    errors: [],
  })
  const { newsCenter, errors, didInitialLoad } = state

  React.useEffect(() => {
    getForm().catch(console.error)
  }, [])

  const getForm = async () => {
    try {
      const { news_center } = await NewsCentersController.getForm({ id: props.newsCenter ? props.newsCenter.id : null })

      setState({
        ...state,
        newsCenter: {
          ...news_center,
          ...props.newsCenter,
        },
        didInitialLoad: true,
      })
    } catch (ex) {
      console.error(ex)
    }
  }


  const onNameChange = (e) => {
    setState({ ...state, newsCenter: { ...newsCenter, name: e.currentTarget.value } })
  }

  const onEnabledChange = () => {
    setState({ ...state, newsCenter: { ...newsCenter, enabled: !newsCenter.enabled } })
  }

  const onCloseClick = () => {
    props.close()
  }

  const onFormSubmit = async (e) => {
    e.preventDefault()

    try {
      let response = null
      if (newsCenter.id) {
        response = await NewsCentersController.update(newsCenter)
      } else {
        response = await NewsCentersController.create(newsCenter)
      }

      if (response.errors) {
        setState({ ...state, errors: response.errors })
      } else {
        if (props.onSubmit) props.onSubmit(response)
        props.close()
      }

    } catch (ex) {
      console.error(ex)
    }
  }

  const onErrorsDismiss = () => {
    setState({ ...state, errors: [] })
  }

  const renderDetails = () => {
    return (
      <div className='grid'>
        <div className='grid-cell with-6col'>
          <div className='form-item'>
            <label>{t('NewsCenterModal::Name')} <span>*</span></label>
            <input
              type='text'
              placeholder={t('NewsCenterModal::Name')}
              onChange={onNameChange}
              value={newsCenter.name}
            />
          </div>
        </div>

        <div className='grid-cell with-6col'>
          <div className='form-item'>
            <label>{t('NewsCenterModal::Enabled')}</label>
            <CheckboxInput
              checked={newsCenter.enabled}
              label={t('NewsCenterModal::Enabled')}
              onChange={onEnabledChange}
            />
          </div>
        </div>
      </div>
    )
  }

  return (
    <ModalWindow>
      <ModalHeader
        title={newsCenter?.id ? t('NewsCenterModal::Update news center') : t('NewsCenterModal::Create news center')}
        onCloseClick={onCloseClick}
      />

      {!didInitialLoad && <ModalLoader />}
      {didInitialLoad && <ModalMiddle>
        <ModalContent>
          <form onSubmit={onFormSubmit}>
            {renderDetails()}
            <input type='submit' style={{ display: 'none' }} onClick={onFormSubmit} />
          </form>
        </ModalContent>

        <div className='modal-footer'>
          <div />
          <div key='main-action' className='popover-wrapper'>
            <TooltipError
              errors={errors}
              onDismiss={onErrorsDismiss}
            />
            <Button
              type='success'
              text={newsCenter?.id ? t('NewsCenterModal::Update') : t('NewsCenterModal::Create')}
              onClick={onFormSubmit}
            />
          </div>
        </div>
      </ModalMiddle>}
    </ModalWindow >
  )
}

const mapStateToProps = (state: AppState): IStateToProps => {
  const {
    authentication: {
      currentUser
    },
    modals: {
      newsCenterModal: {
        newsCenter,
        onSubmit,
      }
    }
  } = state

  return {
    currentUser: currentUser,
    newsCenter: newsCenter,
    onSubmit: onSubmit,
  }
}

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>): IDispatchToProps => {
  return {
    close: () => dispatch(closeNewsCenterModal()),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(NewsCenterModal)